// src/AuxiliaryEngineSubsection.jsx

import React, { useState, useEffect, useRef } from 'react';

export default function AuxiliaryEngineSubsection({
  initialData = [],
  onDataChange,
  isEditMode = false,
}) {
  const [engines, setEngines] = useState(initialData);

  // Ref to skip the first notify after syncing props→state
  const skipNextNotify = useRef(false);

  // 1️⃣ Sync incoming initialData into local state, but only when it truly differs
  useEffect(() => {
    const prev = JSON.stringify(engines);
    const next = JSON.stringify(initialData);
    if (prev !== next) {
      setEngines(initialData);
      skipNextNotify.current = true;
    }
  }, [initialData]);

  // 2️⃣ Notify parent when engines state changes (but skip one after sync)
  useEffect(() => {
    if (skipNextNotify.current) {
      skipNextNotify.current = false;
      return;
    }
    // Only notify when engines really differ from the prop
    if (JSON.stringify(engines) !== JSON.stringify(initialData)) {
      onDataChange(engines);
    }
  }, [engines, initialData, onDataChange]);

  const add = () => {
    setEngines(prev => [
      ...prev,
      {
        make: '',
        model: '',
        bore: '',
        stroke: '',
        mcr: '',
        rpm: '',
        cylinders: '',
        turbochargers: '',
        maxTemperature: '',
        cylinderConstant: '',
      },
    ]);
  };

  const remove = idx => {
    setEngines(prev => prev.filter((_, i) => i !== idx));
  };

  const handle = (idx, field, value) => {
    setEngines(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  return (
    <div className="mb-4">
      <h5 className="fw-bold">Auxiliary Engines</h5>

      {engines.map((eng, idx) => (
        <div key={idx} className="border p-3 mb-3">
          <h6>Auxiliary Engine {idx + 1}</h6>
          <div className="row g-2">
            {[
              ['make', 'Make', 'text'],
              ['model', 'Model', 'text'],
              ['bore', 'Bore', 'number'],
              ['stroke', 'Stroke', 'number'],
              ['mcr', 'MCR', 'number'],
              ['rpm', 'RPM', 'number'],
              ['cylinders', 'Cylinders', 'number'],
              ['turbochargers', 'Turbochargers', 'number'],
              ['maxTemperature', 'Max Temp', 'number'],
              ['cylinderConstant', 'Cylinder Constant', 'number'],
            ].map(([field, label, type]) => (
              <div className="col-md-3" key={field}>
                <label className="form-label">{label}</label>
                <input
                  type={type}
                  className="form-control"
                  value={eng[field] ?? ''}
                  onChange={e => handle(idx, field, e.target.value)}
                />
              </div>
            ))}

            {!isEditMode && (
              <div className="col-md-1 d-flex align-items-end">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => remove(idx)}
                >
                  &times;
                </button>
              </div>
            )}
          </div>
        </div>
      ))}

      {!isEditMode && (
        <button type="button" className="btn btn-secondary" onClick={add}>
          + Add Auxiliary Engine
        </button>
      )}
    </div>
  );
}
